import { Theme } from 'klayowebshared';
import { Component } from 'react';
import { Location, Navigate } from 'react-router-dom';
import { AppContext } from '../common/AppContext';
import { Organization } from '../data/Organization';
import { CurrentUser } from '../data/users/CurrentUser';
import { needsOnboarding } from '../utilities/onboardingStatus';
import { AppPages } from '../router/pages';

// Base props interface for views - subclasses should extend this
// Note: location is provided by RouteComponentProps, so not included here
export interface AbstractViewProps {
  user?: CurrentUser | null;
  organization?: Organization | null;
  org?: Organization | null;
  location?: Location;
  history?: any;
  theme?: typeof Theme;
}

// Using any for props/state to maintain backwards compatibility with existing views
// Subclasses can use stricter types by extending AbstractViewProps
export class AbstractView<
  P extends AbstractViewProps = AbstractViewProps,
  S = any,
> extends Component<P, S> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  page: any;

  constructor(props: P, page: any) {
    super(props);
    this.page = page;
  }

  onNotificationReceived(notif: any, numUnread: any) {}

  componentDidMount() {
    this.updateTitle();
    if (this.props.user) this.onHasUser(this.props.user);
    if (this.props.organization) this.onHasOrganization(this.props.organization);
    if (this.props.user && this.props.organization)
      this.onHasUserAndOrg(this.props.user, this.props.organization);
  }

  onViewChange(location: any) {}

  pathEquals(path: any) {
    return this.props.location?.pathname === path;
  }

  hasQuery(query: any) {
    return this.props.location?.search.includes(query);
  }

  updateTitle() {
    document.title =
      this.page.name +
      (this.props.organization ? ' - ' + this.props.organization.name : '') +
      ' - Klayo';
  }

  onHasUser(user: any) {}

  onHasOrganization(organization: any) {}

  onHasUserAndOrg(user: any, organization: any) {}

  componentDidUpdate(prevProps: any, prevState?: any) {
    if (this.props.location !== prevProps.location) this.onViewChange(this.props.location);

    if (this.props.user !== prevProps.user || this.props.organization !== prevProps.organization) {
      if (this.props.user !== prevProps.user) {
        this.onHasUser(this.props.user);
      }

      if (this.props.organization !== prevProps.organization) {
        this.onHasOrganization(this.props.organization);
        this.updateTitle();
      }

      if (this.props.user && this.props.organization)
        this.onHasUserAndOrg(this.props.user, this.props.organization);
    }
  }

  verifyAccess() {
    const { user, organization } = this.props;
    if (this.page.developerOnly && user && !user.isDeveloper) return false;
    return this.page.hasAccess(user, organization);
  }

  renderAccessDenied() {
    const { user, organization } = this.props;
    if (!user || !organization) return '';

    // If onboarding is incomplete, that takes priority over the default-page
    // redirect. Otherwise an isMyKlayoUser whose onboarding hasn't finished
    // would race AppDataProvider's /onboarding redirect and end up bounced
    // into /my-klayo with empty profile data.
    // Incomplete onboarding routes to /onboarding even when every step can be skipped: that page is
    // the only thing that POSTs CompleteOnboarding (stamping OnboardingCompletedDate and running the
    // HubSpot / LearnUpon pushes), and it self-completes when there is nothing to ask. Not while
    // impersonating, though — needsOnboarding owns that exemption, do not re-derive it here.
    if (needsOnboarding(user)) {
      return <Navigate to='/onboarding' replace />;
    }

    const defaultPage = Object.values(AppPages).find(
      (page: any) => page.isDefault?.(user) && page.hasAccess(user, organization)
    );

    const redirectPath = defaultPage ? `/${defaultPage.slug}` : '/';
    return <Navigate to={redirectPath} replace />;
  }
}
